import React from 'react'
import {
  bulkOperationDownloadResultRequest,
  bulkOperationRequest,
  bulkOperationStatusRequest,
  bulkResultsReset,
} from '../../actions/bulkOperationsActions'
import { CommonProps } from '../../models'
import { BulkOperationsViewStore } from '../../reducers/states'
import BulkMoveForm from '../forms/BulkMoveForm'
import { BulkOperationRequest, BulkOperationStatusResponse } from '../../api/bulkOperations'
import BulkOperationsProgressBar from './BulkOperationsProgress'
import LazyContainer from '../generic/LazyContainer'

interface Props extends CommonProps, BulkOperationsViewStore {
  operationId: string | null
}

class BulkOperationsPanel extends React.Component<Props> {
  constructor(props: Props) {
    super(props)
    this.sendBulkOperation = this.sendBulkOperation.bind(this)
    this.generateCsv = this.generateCsv.bind(this)
    this.goBack = this.goBack.bind(this)
  }

  componentDidMount(): void {
    if (this.props.operationId) {
      this.props.dispatch(
        bulkOperationStatusRequest({
          operationId: this.props.operationId,
        })
      )
    }
  }

  sendBulkOperation(x: BulkOperationRequest) {
    this.props.dispatch(
      bulkOperationRequest({
        dataset: x.dataset,
        operationType: x.operationType,
        parameters: x.parameters,
      })
    )
  }

  generateCsv(operationId: string, status: string | null) {
    this.props.dispatch(bulkOperationDownloadResultRequest({ operationId: operationId, status: status }))
  }

  renderStatus(operationStatusResponse: BulkOperationStatusResponse): string {
    return operationStatusResponse.inProgress ? 'IN PROGRESS' : operationStatusResponse.isCompleted ? 'COMPLETED' : 'INITIALISING'
  }

  goBack() {
    this.props.dispatch(bulkResultsReset({}))
    const urlParams = new URLSearchParams(window.location.search)
    urlParams.delete('operationId')
    window.location.search = urlParams.toString()
  }

  renderForm() {
    return (
      <BulkMoveForm
        input={{
          dataset: null,
          operationType: 'BulkMove',
          parameters: {},
        }}
        onSubmit={this.sendBulkOperation}></BulkMoveForm>
    )
  }

  renderOperationStatus() {
    const status = this.props.bulkOperationStatusResponse
    const operationStatus = this.renderStatus(this.props.bulkOperationStatusResponse)

    const statusColor =
      status.failedItems > 0
        ? 'var(--color-danger, #d64949)'
        : status.inProgress
        ? 'var(--color-warning, #003952)'
        : 'var(--color-success, #4CAF50)'

    const cardStyle = {
      border: `1px solid ${statusColor}`,
      padding: '20px',
      margin: '20px 0',
      backgroundColor: 'var(--color-bg-light, #f9f9f9)',
    }

    const headerStyle = {
      borderBottom: `2px solid ${statusColor}`,
      paddingBottom: '10px',
      marginBottom: '15px',
      fontSize: '1.4em',
      fontWeight: 600,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }

    const dataGridStyle = {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '10px 20px',
    }

    const labelStyle = {
      fontWeight: 500,
      color: 'var(--color-text-secondary, #555)',
    }

    const valueStyle = {
      fontWeight: 600,
      color: 'var(--color-text-primary, #333)',
    }
    const buttonPrimaryStyle = {
      padding: '10px 20px',
      backgroundColor: 'var(--color-primary, #003952)',
      color: 'white',
      border: 'none',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: '1em',
      marginBotton: '20px',
      width: '30%',
    }
    const buttonContainer = {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      padding: '10px',
    }

    return (
      <LazyContainer isLoading={this.props.isLoading}>
        <div style={cardStyle}>
          <div style={headerStyle}>
            <span>
              {'Bulk Operation'} {operationStatus}
            </span>
            <span style={{ fontSize: '0.8em', color: statusColor }}></span>
          </div>

          {!status.isCompleted && (
            <div style={{ marginBottom: '20px' }}>
              <BulkOperationsProgressBar
                itemsProcessed={status.completedItems + status.failedItems}
                totalItems={status.totalItems}
                updatedAt={status.updatedAt}
                isInitialiasing={operationStatus == 'INITIALISING'}
              />
            </div>
          )}
          <div style={dataGridStyle}>
            {/* Column 1: Core Identifiers */}
            <div>
              <p>
                <span style={labelStyle}>Operation ID:</span> <span style={valueStyle}>{status.operationId}</span>
              </p>
              <p>
                <span style={labelStyle}>Operation Type:</span>{' '}
                <span style={valueStyle}>{status.operationType || this.props.bulkOperationResponse.operationType}</span>
              </p>
            </div>

            {/* Column 2: Item Counts */}
            <div>
              {operationStatus == 'IN PROGRESS' && (
                <div>
                  <p>
                    <span style={{ ...valueStyle, fontSize: '20px' }}>Processing items</span>
                  </p>
                  <p>
                    <span style={labelStyle}>In Progress:</span>{' '}
                    <span style={{ ...valueStyle, color: 'var(--color-info, #007ace)' }}>{status.inProgressItems}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Column 3: Status Details */}
            <div>
              {operationStatus != 'INITIALISING' && (
                <div>
                  <p>
                    <span style={{ ...valueStyle, fontSize: '20px' }}>Processed Items</span>
                  </p>
                  <p>
                    <span style={labelStyle}>Failed:</span>{' '}
                    <span style={{ ...valueStyle, color: 'var(--color-danger, #d64949)' }}>{status.failedItems}</span>
                    {operationStatus == 'COMPLETED' && status.failedItems > 0 && (
                      <a
                        onClick={() => this.generateCsv(status.operationId, 'Failed')}
                        style={{ color: 'var(--color-primary, #007bff)', textDecoration: 'underline' }}>
                        Export failed items
                      </a>
                    )}
                  </p>
                  <p>
                    <span style={labelStyle}>Completed:</span>{' '}
                    <span style={{ ...valueStyle, color: 'var(--color-success, #4CAF50)' }}>{status.completedItems}</span>
                  </p>
                  <p>
                    <span style={labelStyle}>Total Items:</span> <span style={valueStyle}>{status.totalItems}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
          <div style={buttonContainer}>
            {operationStatus == 'COMPLETED' && (
              <button onClick={() => this.generateCsv(status.operationId, null)} style={buttonPrimaryStyle}>
                Export as CSV
              </button>
            )}
            <button onClick={() => this.goBack()}>Back</button>
          </div>
        </div>
      </LazyContainer>
    )
  }

  render() {
    if ((this.props.operationId && this.props.bulkOperationStatusResponse.operationId) || this.props.isLoading) {
      return this.renderOperationStatus()
    }
    return this.renderForm()
  }
}

export default BulkOperationsPanel
